import constants from "../../../../../../appConstants";
import { Button } from "@material-ui/core";
import { getSpecialNeeds, SpecialNeedsRenderer } from "../../../../../dataGrid/cellRenderer";
import {
  mergeUserConfig,
  svcValueGetter,
  hasDefaultDeliveryWindow,
} from "../../../../../dataGrid/gridUtilities";
import {
  MultiSelectFilter,
  DateTimeFilter,
  StaticFilter,
} from "../../../../../dataGrid/columnFilters";
import {
  getDeliveryStatusFromShipmentDelCode,
  formatNumber,
  updateGridColumnHeaderNames,
  getPickupStopStatus,
} from "../../../utils/utils";
import { unassignPickup } from "../../utils/pickupActions";
import { getStopArrivalTime } from "../../../../Reports/DriverPerformance/util";
import { gridShortHeaderNamesConfig } from "./constants";

export default class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = "GridConfiguration";
    this.config = GridConfig.getConfiguration(config);
  }

  static getConfiguration({
    userConfig,
    featureFlags: { enableColumnShortForm },
  }) {
    const defaultConfig = GridConfig.getDefaultConfiguration();
    const mergedConfig = mergeUserConfig({
      defaultConfig,
      userConfig,
    });
    return updateGridColumnHeaderNames({
      gridShortHeaderNamesConfig,
      columnConfig: mergedConfig,
      enableColumnShortForm,
    });
  }

  static isPickupStop(data) {
    return data.stopType === constants.STOP_TYPE.Pickup;
  }

  static isStopCompleted(data) {
    return data.isStopCompleted;
  }

  static stopTypeRenderer(props) {
    return <div>{GridConfig.isPickupStop(props.data) ? "P" : "D"}</div>;
  }

  static StatusValueGetter({ data }) {
    if (GridConfig.isStopCompleted(data)) {
      if(GridConfig.isPickupStop(data)){
        return getPickupStopStatus(data)
      }
      const deliveryStatus = getDeliveryStatusFromShipmentDelCode(
        data.deliveryCode
      );
      switch (deliveryStatus) {
        case constants.DELIVERY_STATUS.RETURN:
          return `${deliveryStatus} (${data.returnCode})`;
        case constants.DELIVERY_STATUS.DELIVERY_EXCEPTION:
          return `${deliveryStatus} (${data.exceptionCode})`;
        default:
          return deliveryStatus;
      }
    }
    return constants.DELIVERY_STATUS.PENDING;
  }

  static isReturnDelivery({ data }) {
    return (
      getDeliveryStatusFromShipmentDelCode(data.deliveryCode) ===
      constants.DELIVERY_STATUS.RETURN
    );
  }

  static formatDeliveryWindow(params) {
    const { deliveryWindow } = params.data;
    const isDefaultDeliveryWindow = deliveryWindow
      ? hasDefaultDeliveryWindow(deliveryWindow)
      : false;
    if (isDefaultDeliveryWindow && !params.data.isApt) {
      return "";
    }
    return deliveryWindow;
  }

  static getReadyClosetime = (params) => {
    const { readyTime, closeTime } = params.data;
    if (readyTime && closeTime) {
      return `${readyTime} - ${closeTime}`;
    }
    return "";
  };

  static getSpecialNeedsOption(params) {
    return params.context.specialNeedsOptions;
  }

  static getCommaSeparatedProNumber(params) {
    if (params.data.stopType === constants.STOP_TYPE.Pickup) {
      return params.data.shipments.map((pro) => pro.proId).join(", ");
    }
    return params.data.proId;
  }

  static UnassignButtonRenderer(props) {
    return (
      <div>
        {props.data.stopType === constants.STOP_TYPE.Pickup &&
        !props.data.isStopCompleted ? (
          <Button
            className={"dispatch-grid-active-button"}
            onClick={() =>
              unassignPickup({
                pickupIds: [props.data.pickupId],
                driverId: props.data.driver.driverId,
                terminalCode: props.context.terminalCode,
                glEventHub: props.context.glEventHub,
              })
            }
            data-automationid="Unassign"
          >
            Unassign
          </Button>
        ) : (
          ""
        )}
      </div>
    );
  }

  static getDefaultConfiguration() {
    return {
      title: constants.gridNames.DriverInfoGrid,
      keyColumn: "driverInfoPopup",
      rowSelection: "multiple",
      rowDeselection: true,
      defaultColDef: {
        lockPinned: true,
        sortable: true,
        filter: true,
        resizable: true,
      },
      rowClassRules: {
        violationRowBGColor: GridConfig.isReturnDelivery,
      },
      columns: [
        {
          headerName: "Action",
          headerTooltip: "Assign/UnAssign Pickup",
          minWidth: constants.columnWidth.ASSIGN_ACTION,
          cellClass: "gridCell-leftAlign",
          colId: "actionCol",
          cellRendererFramework: GridConfig.UnassignButtonRenderer,
          pinned: "left",
          lockPosition: true,
          filter: false,
          sortable: false,
        },
        {
          headerName: "Seq",
          field: "stopSequence",
          headerTooltip: "Sequence",
          minWidth: constants.columnWidth.DRIVER_INFO_SMALL,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "P/D",
          headerTooltip: "Pickup or Delivery",
          minWidth: constants.columnWidth.DRIVER_INFO_SMALL,
          cellClass: "gridCell-leftAlign",
          cellRendererFramework: GridConfig.stopTypeRenderer,
          filterFramework: MultiSelectFilter,
          field: "stopType",
          pinned: "left",
          lockPosition: true,
        },
        {
          headerName: "Pro #",
          headerTooltip: "Pro Number",
          field: "proId",
          minWidth: constants.columnWidth.PRO_NUMBER,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          filterParams: {
            filterType: constants.FILTER_TYPES.STRING,
          },
          valueGetter: GridConfig.getCommaSeparatedProNumber,
          tooltipValueGetter: (params) => params.value,
        },
        {
          headerName: "Pickup #",
          headerTooltip: "Pickup Number",
          field: "pickupNumber",
          minWidth: constants.columnWidth.W_110,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          filterParams: {
            filterType: constants.FILTER_TYPES.STRING,
          },
        },
        {
          headerName: "Status",
          headerTooltip: "Status",
          minWidth: constants.columnWidth.STATUS,
          cellClass: "gridCell-leftAlign",
          valueGetter: GridConfig.StatusValueGetter,
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Spl Needs",
          minWidth: constants.columnWidth.SPL_NEED,
          valueGetter: getSpecialNeeds,
          headerTooltip: "Special Needs",
          cellClass: "gridCell-leftAlign",
          cellRendererFramework: SpecialNeedsRenderer,
          filterFramework: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getSpecialNeedsOption,
          },
        },
        {
          headerName: "Customer",
          headerTooltip: "Customer Name",
          field: "name",
          minWidth: constants.columnWidth.CUSTOMER_NAME,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Address",
          headerTooltip: "Address",
          field: "address",
          minWidth: constants.columnWidth.DRIVER_ADDRESS,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "City",
          headerTooltip: "City",
          field: "city",
          minWidth: constants.columnWidth.CITY,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "State",
          headerTooltip: "State",
          field: "state",
          minWidth: constants.columnWidth.STATE,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Zip",
          headerTooltip: "Zip",
          field: "zip",
          minWidth: constants.columnWidth.ZIP_CODE,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Weight",
          headerTooltip: "Weight",
          field: "weight",
          minWidth: constants.columnWidth.W_90,
          cellClass: "gridCell-leftAlign",
          valueGetter: (params) => formatNumber(params.data.weight),
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Cube",
          headerTooltip: "Cube",
          field: "cube",
          minWidth: constants.columnWidth.W_85,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Pcs",
          headerTooltip: "Pieces",
          field: "pieces",
          minWidth: constants.columnWidth.DRIVER_INFO_SMALL,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "ETA",
          headerTooltip: "ETA",
          valueGetter: (params) => getStopArrivalTime(params.data),
          minWidth: constants.columnWidth.ARRIVAL_TIME,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "ETD",
          headerTooltip: "ETD",
          field: "departureTime",
          minWidth: constants.columnWidth.EXPECTED_DEPARTURE_TIME,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Ready Close Time",
          headerTooltip: "Ready Close Time",
          minWidth: constants.columnWidth.DRIVER_INFO,
          cellClass: "gridCell-leftAlign",
          valueGetter: GridConfig.getReadyClosetime,
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
          },
        },
        {
          headerName: "Apt/Del Window",
          headerTooltip: "Appointment/Delivery Window",
          minWidth: constants.columnWidth.DRIVER_INFO,
          cellClass: "gridCell-leftAlign",
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
          },
          valueFormatter: GridConfig.formatDeliveryWindow,
        },
        {
          headerName: "SVC",
          valueGetter: svcValueGetter,
          minWidth: constants.columnWidth.W_120,
          headerTooltip: "Service Level",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Ldg Zone",
          headerTooltip: "Loading Zone",
          field: "loadingZone",
          minWidth: constants.columnWidth.LDG_ZONE,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
      ],
    };
  }
}
